/**
 * 绝对路径: D:/XXY/MeiShan_Project/frontend/shared/composables/useConstants.ts
 * 常量选项统一提供器：服务类型/餐段。
 * - 来源：/api/v1/constants/*（constantsApi）
 * - 缓存：首次拉取后保存在内存，提供 refresh() 主动刷新
 */

import { ref } from 'vue'
import { constantsApi } from '../api/constants'
import { MEAL_SEGMENT_BUSINESS_PRIORITY } from '../utils/meal-segment'

export { MEAL_SEGMENT_BUSINESS_PRIORITY }

export interface SelectOption {
  label: string
  value: string | number
  /** 排序权重（后端 sort_order，数字越小越靠前） */
  sortOrder?: number
}

/**
 * 对餐段选项按标准时序排序
 *
 * L1: 5大标准餐段按业务时序（早餐→午餐→下午茶→晚餐→宵夜），自定义餐段排后
 * L2: sortOrder（后端 sort_order），数字越小越靠前
 * L3: code 字典序（兜底稳定排序）
 */
export function sortMealSegmentOptions(options: SelectOption[]): SelectOption[] {
  const CUSTOM_BASE = 100
  return [...options].sort((a, b) => {
    const codeA = String(a.value)
    const codeB = String(b.value)
    const isStdA = codeA in MEAL_SEGMENT_BUSINESS_PRIORITY
    const isStdB = codeB in MEAL_SEGMENT_BUSINESS_PRIORITY
    const l1A = isStdA ? MEAL_SEGMENT_BUSINESS_PRIORITY[codeA] : CUSTOM_BASE
    const l1B = isStdB ? MEAL_SEGMENT_BUSINESS_PRIORITY[codeB] : CUSTOM_BASE
    if (l1A !== l1B) return l1A - l1B
    const l2A = a.sortOrder ?? 999
    const l2B = b.sortOrder ?? 999
    if (l2A !== l2B) return l2A - l2B
    return codeA.localeCompare(codeB)
  })
}

const mealSegmentOptionsCache = ref<SelectOption[] | null>(null)
const serviceTypeOptionsCache = ref<SelectOption[] | null>(null)
const consumptionTypeOptionsCache = ref<SelectOption[] | null>(null)

/**
 * 餐段兜底（仅用于“常量接口无数据/请求失败”时的 UI 保底）。
 *
 * ⚠️ 重要：必须使用后端真实 code（契约唯一真源），禁止使用 breakfast/lunch/dinner 这类前端假枚举，
 * 否则会导致写接口校验失败、回显错乱、筛选无结果等问题。
 *
 * 当前项目常用启用值（来源：运营端“常量目录管理 → 餐段类别”）：
 * - ZC0002 早餐
 * - WC0003 午餐
 * - WC0004 晚餐
 * - XY0002 宵夜
 * - XWC002 下午茶
 */
const FALLBACK_MEAL_SEGMENT_OPTIONS: SelectOption[] = [
  { label: '早餐', value: 'ZC0002' },
  { label: '午餐', value: 'WC0003' },
  { label: '下午茶', value: 'XWC002' },
  { label: '晚餐', value: 'WC0004' },
  { label: '宵夜', value: 'XY0002' }
]

export function useMealSegmentOptions() {
  const loading = ref(false)
  const options = ref<SelectOption[]>([])
  const codeToLabel = ref<Map<string, string>>(new Map())
  const idToLabel = ref<Map<number, string>>(new Map())

  async function ensure() {
    if (mealSegmentOptionsCache.value) {
      options.value = mealSegmentOptionsCache.value
      return
    }
    loading.value = true
    try {
      // 优先使用 tree 口径，避免“有子餐段（如 午餐2）但列表只返回 3 个根节点”的问题
      // 约定：全系统统一从常量接口消费“启用的餐段”，不在各页面写死三餐
      let items: unknown[] = []
      try {
        items = await constantsApi.treeMealSegments(true)
      } catch {
        items = await constantsApi.listMealSegments(true)
      }
      const opts: SelectOption[] = []
      const c2l = new Map<string, string>()
      const i2l = new Map<number, string>()
      const walk = (arr: unknown[]) => {
        for (const it of arr) {
          if (!it || typeof it !== 'object' || Array.isArray(it)) continue
          const r = it as Record<string, unknown>
          const code = r.code
          const label = String(r.label ?? r.code ?? '')
          if (code == null) continue
          const sortOrder = typeof r.sort_order === 'number' ? r.sort_order : undefined
          opts.push({ label, value: code as string | number, sortOrder })
          if (typeof code === 'string') c2l.set(code, label)
          if (typeof r.id === 'number') i2l.set(r.id, label)
          if (Array.isArray(r.children) && r.children.length) walk(r.children)
        }
      }
      walk(Array.isArray(items) ? items : [])
      // 若后端暂时无数据，提供回退，避免调用处空数组
      if (opts.length === 0) {
        // ⚠️ 同步填充 codeToLabel 映射，避免 fallback 时"有选项但无标签映射"
        for (const it of FALLBACK_MEAL_SEGMENT_OPTIONS) {
          const code = String(it.value)
          c2l.set(code, it.label)
          opts.push({ label: it.label, value: it.value })
        }
      }
      // ⭐ 按 sort_order 排序，保证 早餐→午餐→晚餐→宵夜→下午茶 标准顺序
      const sorted = sortMealSegmentOptions(opts)
      mealSegmentOptionsCache.value = sorted
      options.value = sorted
      codeToLabel.value = c2l
      idToLabel.value = i2l
    } catch (e) {
      // 请求失败兜底：提供最小集合，保证UI可用
      const fallback: SelectOption[] = [...FALLBACK_MEAL_SEGMENT_OPTIONS]
      mealSegmentOptionsCache.value = fallback
      options.value = fallback
      codeToLabel.value = new Map(FALLBACK_MEAL_SEGMENT_OPTIONS.map(it => [String(it.value), it.label]))
    } finally {
      loading.value = false
    }
  }

  async function refresh() {
    mealSegmentOptionsCache.value = null
    await ensure()
  }

  ensure().catch(() => {})

  return { loading, options, codeToLabel, idToLabel, refresh }
}

export function useServiceTypeOptions() {
  const loading = ref(false)
  const options = ref<SelectOption[]>([])
  const codeToLabel = ref<Map<string, string>>(new Map())
  const idToLabel = ref<Map<number, string>>(new Map())

  async function ensure() {
    if (serviceTypeOptionsCache.value) {
      options.value = serviceTypeOptionsCache.value
      return
    }
    loading.value = true
    try {
      const items = await constantsApi.listServiceTypes(true)
      const opts: SelectOption[] = []
      const c2l = new Map<string, string>()
      const i2l = new Map<number, string>()
      const walk = (arr: unknown[]) => {
        for (const it of arr) {
          if (!it || typeof it !== 'object' || Array.isArray(it)) continue
          const r = it as Record<string, unknown>
          const code = r.code
          const label = String(r.label ?? r.code ?? '')
          if (code == null) continue
          opts.push({ label, value: code as string | number })
          if (typeof code === 'string') c2l.set(code, label)
          if (typeof r.id === 'number') i2l.set(r.id, label)
          if (Array.isArray(r.children) && r.children.length) walk(r.children)
        }
      }
      walk(Array.isArray(items) ? items : [])
      serviceTypeOptionsCache.value = opts
      options.value = opts
      codeToLabel.value = c2l
      idToLabel.value = i2l
    } finally {
      loading.value = false
    }
  }

  async function refresh() {
    serviceTypeOptionsCache.value = null
    await ensure()
  }

  ensure().catch(() => {})

  return { loading, options, codeToLabel, idToLabel, refresh }
}

/**
 * 消费类型（消费场景）兜底（仅用于“常量接口无数据/请求失败”时的 UI 保底）。
 *
 * ⚠️ 必须使用后端真实 code（契约唯一真源），禁止使用 online_ordering 等前端 key。
 */
const FALLBACK_CONSUMPTION_TYPE_OPTIONS: SelectOption[] = [
  { label: '线上订餐', value: 'ALWS01' },
  { label: '到店消费', value: 'BLWM01' },
  { label: '职场分餐', value: 'CLGR01' },
  { label: '线上商城', value: 'DLHD01' },
  { label: '零食柜', value: 'ELXC01' },
]

export function useConsumptionTypeOptions() {
  const loading = ref(false)
  const options = ref<SelectOption[]>([])
  const codeToLabel = ref<Map<string, string>>(new Map())

  async function ensure() {
    if (consumptionTypeOptionsCache.value) {
      options.value = consumptionTypeOptionsCache.value
      return
    }
    loading.value = true
    try {
      const mapping = await constantsApi.getMappings()
      const items = Array.isArray(mapping?.consumption_types) ? mapping.consumption_types : []
      const opts: SelectOption[] = []
      const c2l = new Map<string, string>()
      for (const it of items) {
        const code = String((it as unknown as { code?: unknown }).code ?? '')
        const label = String((it as unknown as { label?: unknown }).label ?? code)
        if (!code) continue
        opts.push({ label, value: code })
        c2l.set(code, label)
      }
      if (opts.length === 0) {
        for (const it of FALLBACK_CONSUMPTION_TYPE_OPTIONS) {
          const code = String(it.value)
          c2l.set(code, it.label)
          opts.push({ label: it.label, value: it.value })
        }
      }
      consumptionTypeOptionsCache.value = opts
      options.value = opts
      codeToLabel.value = c2l
    } catch {
      const fallback: SelectOption[] = [...FALLBACK_CONSUMPTION_TYPE_OPTIONS]
      consumptionTypeOptionsCache.value = fallback
      options.value = fallback
      codeToLabel.value = new Map(FALLBACK_CONSUMPTION_TYPE_OPTIONS.map(it => [String(it.value), it.label]))
    } finally {
      loading.value = false
    }
  }

  async function refresh() {
    consumptionTypeOptionsCache.value = null
    await ensure()
  }

  ensure().catch(() => {})

  return { loading, options, codeToLabel, refresh }
}


